<template>
    <div class="rounded-xl border border-border px-4 py-10 md:px-10 bg-surface-raised">
        <p v-if="skipped" class="body-small text-ink-muted">
            {{ t("chat.rolePlaySummary.breakdown.skipped") }}
        </p>
        <div v-else class="flex flex-col md:grid md:grid-cols-3 gap-y-3 md:gap-x-10 md:gap-y-6" role="table" :aria-label="t('chat.rolePlaySummary.breakdown.tableLabel')">
            <div role="row" class="contents">
                <div role="columnheader" class="body-small hidden md:block">{{ t("chat.rolePlaySummary.breakdown.skillHeader") }}</div>
                <div role="columnheader" class="body-small hidden md:block">{{ t("chat.rolePlaySummary.breakdown.scoreHeader") }}</div>
                <div role="columnheader" class="body-small hidden md:block">{{ t("chat.rolePlaySummary.breakdown.improveHeader") }}</div>
            </div>
            <hr class="col-span-3 hidden md:block" role="presentation" />
            <template v-for="({ name, score, rationale, how_to_improve, state }, index) in skills" :key="index">
                <div role="row" class="contents">
                    <div role="cell">
                        <p v-if="state.name === STATE.LOADED" class="body-small-bold">{{ name }}</p>
                        <SkeletonLoader v-else-if="state.name === STATE.LOADING" class="w-full h-[0.75lh] rounded-md my-1" />
                        <span class="inline-flex gap-2" :aria-label="t('chat.rolePlaySummary.breakdown.ratingLabel', { score: score ?? 0, max: 5 })" role="img">
                            <i
                                v-for="i in 5"
                                :key="i"
                                class="bi bi-star-fill transition-colors"
                                :class="[!!score && i <= score && starColors[score] ? starColors[score] : 'text-rating-empty']"
                                aria-hidden="true" />
                        </span>
                    </div>
                    <div role="cell">
                        <Transition name="fade" mode="out-in">
                            <p v-if="state.rationale === STATE.LOADED && rationale" class="body-small font-normal fs-mask">{{ rationale }}</p>
                            <p v-else-if="state.rationale === STATE.LOADED && !rationale" class="body-small font-normal">{{ t("chat.rolePlaySummary.notEnoughData") }}</p>
                            <div v-else-if="state.rationale === STATE.LOADING">
                                <SkeletonLoader v-for="i in 3" :key="i" class="h-[0.75lh] rounded-md my-1" :class="[i === 3 ? 'w-3/5' : 'w-full']" />
                            </div>
                        </Transition>
                    </div>
                    <div role="cell">
                        <p class="body-small-bold block md:hidden">{{ t("chat.rolePlaySummary.breakdown.improveHeader") }}</p>
                        <Transition name="fade" mode="out-in">
                            <p v-if="state.how_to_improve === STATE.LOADED && how_to_improve" class="body-small font-normal fs-mask">{{ how_to_improve }}</p>
                            <p v-else-if="state.how_to_improve === STATE.LOADED && !how_to_improve" class="body-small font-normal">{{ t("chat.rolePlaySummary.notEnoughData") }}</p>
                            <div v-else-if="state.how_to_improve === STATE.LOADING">
                                <SkeletonLoader v-for="i in 3" :key="i" class="h-[0.75lh] rounded-md my-1" :class="[i === 3 ? 'w-3/5' : 'w-full']" />
                            </div>
                        </Transition>
                    </div>
                </div>
                <hr v-if="index !== skills.length - 1" class="col-span-3" role="presentation" />
            </template>
        </div>
    </div>
</template>

<script setup>
import SkeletonLoader from "~vue/components/SkeletonLoader.vue";
import { STATE } from "~vue/pages/RolePlaySummary.vue";
import { useI18n } from "vue-i18n";

const { skills } = defineProps({
    skills: {
        type: Array,
        default: () => [],
    },
    skipped: { type: Boolean, default: false },
});

const { t } = useI18n();

const starColors = {
    1: "text-rating-low",
    2: "text-rating-low",
    3: "text-rating-mid",
    4: "text-rating-high",
    5: "text-rating-high",
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
